/* eslint-disable import/no-unused-modules */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { useFetcher } from '@remix-run/react';
import type { StripeError } from './StripePopupShared';
import type { PaymentIntentCreateAction } from '~/routes/remix-api.payment-intent.create';
import {
    assertSuccessResponse,
    assertErrorResponse,
} from '@modules/root/graphql/responses';
import { usePaymentPopup } from '../../hooks/usePaymentPopup';
import { getSurchargeConfig, removeSurcharge } from '../../util/utils';
import type { Currency } from '../../type/types';
import {
    buildElementsOptions,
    useStripeInstance,
    useBackButtonClose,
    StripePaymentForm,
    StripePopupDialog,
} from './StripePopupShared';

/** Extended Stripe type with confirmPayment method for payment intents */
interface StripeWithConfirmPayment extends Stripe {
    confirmPayment(options: {
        elements: StripeElements;
        confirmParams: { return_url: string };
        redirect: 'if_required';
    }): Promise<{ error?: StripeError }>;
}

/**
 * Stripe payment popup component with payment element.
 * Creates a payment intent via fetcher and renders the shared Stripe popup dialog.
 */
export const StripePaymentPopup: React.FC<{
    onPaymentSuccess?: () => void;
    onPaymentError?: (error: string) => void;
}> = ({ onPaymentSuccess, onPaymentError }) => {
    const {
        isPaymentPopupOpen,
        closePopup,
        currentUrlSegment,
        stripeAccount,
        currentPaymentId,
        publishableKey,
    } = usePaymentPopup();

    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [paymentIntentData, setPaymentIntentData] = useState<{
        amount: number;
        currency: string;
    } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    /** Error message to display when payment intent creation fails */
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const hasCreatedPaymentIntent = useRef(false);

    const stripePromise = useStripeInstance(stripeAccount, publishableKey);

    const paymentIntentFetcher = useFetcher<PaymentIntentCreateAction>();

    // Create payment intent when popup opens
    useEffect(() => {
        if (
            isPaymentPopupOpen &&
            currentUrlSegment &&
            currentPaymentId &&
            !hasCreatedPaymentIntent.current &&
            paymentIntentFetcher.state === 'idle'
        ) {
            setIsLoading(true);
            hasCreatedPaymentIntent.current = true;
            paymentIntentFetcher.submit(
                {
                    invoiceUuid: currentUrlSegment,
                    invoicePaymentId: currentPaymentId,
                },
                {
                    method: 'post',
                    action: '/remix-api/payment-intent/create',
                }
            );
        } else if (!isPaymentPopupOpen) {
            // Reset when popup closes
            setClientSecret(null);
            setPaymentIntentData(null);
            setIsLoading(false);
            setErrorMessage(null);
            hasCreatedPaymentIntent.current = false;
        }
    }, [
        isPaymentPopupOpen,
        currentUrlSegment,
        currentPaymentId,
        paymentIntentFetcher,
    ]);

    // Handle payment intent creation response
    useEffect(() => {
        if (
            paymentIntentFetcher.state === 'idle' &&
            paymentIntentFetcher.data
        ) {
            const isSuccess = assertSuccessResponse(paymentIntentFetcher.data);
            const isError = assertErrorResponse(paymentIntentFetcher.data);

            if (isSuccess) {
                if (
                    'data' in paymentIntentFetcher.data &&
                    paymentIntentFetcher.data.data
                ) {
                    const {
                        clientSecret: newClientSecret,
                        amount: intentAmount,
                        currency: intentCurrency,
                    } = paymentIntentFetcher.data.data;
                    setClientSecret(newClientSecret);
                    setPaymentIntentData({
                        amount: intentAmount,
                        currency: intentCurrency,
                    });
                    setErrorMessage(null);
                }
            } else if (isError) {
                if ('message' in paymentIntentFetcher.data) {
                    const error = String(paymentIntentFetcher.data.message);
                    setErrorMessage(error);
                    onPaymentError?.(error);
                }
            }
            setIsLoading(false);
        }
    }, [paymentIntentFetcher.state, paymentIntentFetcher.data, onPaymentError]);

    useBackButtonClose(isPaymentPopupOpen, closePopup, 'paymentPopup');

    const options = buildElementsOptions(clientSecret);

    // Compute surcharge from payment intent data (amount is in cents from Stripe)
    const paymentData = useMemo(() => {
        if (!paymentIntentData) {
            return null;
        }
        const amountWithSurcharge = paymentIntentData.amount / 100;
        const surchargeConfig = getSurchargeConfig(
            paymentIntentData.currency.toUpperCase() as Currency
        );
        const amount = removeSurcharge(amountWithSurcharge, surchargeConfig);
        return {
            amount,
            currency: paymentIntentData.currency,
            amountWithSurcharge,
            surchargeConfig,
        };
    }, [paymentIntentData]);

    /** Confirm the payment intent using Stripe.js */
    const confirmPayment = async (
        stripe: Stripe,
        elements: StripeElements
    ): Promise<{ error?: StripeError }> => {
        return (stripe as StripeWithConfirmPayment).confirmPayment({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: 'if_required',
        });
    };

    return (
        <StripePopupDialog
            isOpen={isPaymentPopupOpen}
            closePopup={closePopup}
            paymentData={paymentData}
            isLoading={isLoading || paymentIntentFetcher.state === 'submitting'}
            loadingMessage="Processing payment details..."
            errorMessage={errorMessage}
            stripePromise={stripePromise}
            options={options}
            stripeAccount={stripeAccount}
        >
            <StripePaymentForm
                confirmAction={confirmPayment}
                invoicePaymentId={
                    currentPaymentId ? String(currentPaymentId) : null
                }
                closePopup={closePopup}
                successMessage="Payment successful!"
                failureMessage="Payment failed"
                formId="payment-form"
                onSuccess={() => {
                    closePopup();
                    onPaymentSuccess?.();
                }}
                onError={(error) => {
                    onPaymentError?.(error);
                }}
            />
        </StripePopupDialog>
    );
};
